<template>
  <div>
    <Breadcrumbs main="Court" title="Court Diary" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Court Diary</h5>
                <span class="text-muted">Manage court calendar events and reminders</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadEntries"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add Entry
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading court diary entries...
              </div>
              <div v-else-if="sortedEntries.length === 0" class="text-center py-4 text-muted">
                No court diary entries found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Court</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Matter</th>
                      <th>Status</th>
                      <th>Reminder</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in sortedEntries" :key="entry.courtDiaryId">
                      <td>{{ entry.courtDiaryId }}</td>
                      <td><strong>{{ entry.title || '—' }}</strong></td>
                      <td>{{ entry.eventType || '—' }}</td>
                      <td>{{ entry.courtName || '—' }}</td>
                      <td>{{ formatDate(entry.eventDate) }}</td>
                      <td>{{ formatTime(entry.eventTime) }}</td>
                      <td>{{ matterLabel(entry.matterId) }}</td>
                      <td>
                        <span class="badge" :class="statusBadgeClass(entry.status)">
                          {{ entry.status || '—' }}
                        </span>
                      </td>
                      <td>
                        <span
                          v-if="entry.reminder"
                          class="badge"
                          :class="entry.reminderSent ? 'badge-secondary' : 'badge-info'"
                        >
                          {{ entry.reminderSent ? 'Sent' : `${entry.reminderMinutes}m` }}
                        </span>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(entry)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(entry)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Entry' : 'Add Entry' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>

                <h6 class="text-muted mb-3">Event details</h6>
                <div class="row">
                  <div class="col-md-8">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Title</label>
                      <input
                        v-model="form.title"
                        class="form-control"
                        type="text"
                        maxlength="255"
                        placeholder="e.g. Hearing — Mwangi v. Acme Ltd"
                      >
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Event type</label>
                      <select v-model="form.eventType" class="form-select">
                        <option value="">Select type</option>
                        <option v-for="type in eventTypeOptions" :key="type" :value="type">
                          {{ type }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Linked matter</label>
                      <select v-model.number="form.matterId" class="form-select">
                        <option :value="null">None</option>
                        <option
                          v-for="matter in matters"
                          :key="matter.matterId"
                          :value="matter.matterId"
                        >
                          {{ matterLabel(matter.matterId) }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Status</label>
                      <select v-model="form.status" class="form-select">
                        <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Court / venue</label>
                      <input
                        v-model="form.courtName"
                        class="form-control"
                        type="text"
                        maxlength="255"
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Event date</label>
                      <input
                        v-model="form.eventDate"
                        class="form-control"
                        type="date"
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Event time</label>
                      <input
                        v-model="form.eventTime"
                        class="form-control"
                        type="time"
                        step="1"
                      >
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Notes</label>
                      <textarea v-model="form.notes" class="form-control" rows="3" />
                    </div>
                  </div>
                </div>

                <h6 class="text-muted mb-3 mt-2">Reminders</h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-check form-switch mb-3">
                      <input
                        id="reminder-enabled"
                        v-model="form.reminder"
                        class="form-check-input"
                        type="checkbox"
                      >
                      <label class="form-check-label" for="reminder-enabled">
                        Enable reminder
                      </label>
                    </div>
                  </div>
                  <div v-if="form.reminder" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Remind before (minutes)</label>
                      <select v-model.number="form.reminderMinutes" class="form-select">
                        <option v-for="m in reminderMinuteOptions" :key="m" :value="m">
                          {{ reminderLabel(m) }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <template v-if="form.reminder">
                    <div class="col-md-4">
                      <div class="form-check form-switch mb-3">
                        <input
                          id="email-notification"
                          v-model="form.emailNotification"
                          class="form-check-input"
                          type="checkbox"
                        >
                        <label class="form-check-label" for="email-notification">
                          Email notification
                        </label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check form-switch mb-3">
                        <input
                          id="sms-notification"
                          v-model="form.smsNotification"
                          class="form-check-input"
                          type="checkbox"
                        >
                        <label class="form-check-label" for="sms-notification">
                          SMS notification
                        </label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check form-switch mb-3">
                        <input
                          id="desktop-notification"
                          v-model="form.desktopNotification"
                          class="form-check-input"
                          type="checkbox"
                        >
                        <label class="form-check-label" for="desktop-notification">
                          Desktop notification
                        </label>
                      </div>
                    </div>
                  </template>
                  <div v-if="isEditing && form.reminderSent" class="col-12">
                    <p class="text-muted f-12 mb-0">
                      Reminder has already been sent for this entry.
                    </p>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Entry') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="entryToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Entry</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="entryToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete
                <strong>{{ entryToDelete.title || `Entry #${entryToDelete.courtDiaryId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The entry will be soft-deleted and hidden from the list. The record is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="entryToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="entryToDelete" class="modal-backdrop fade show" @click="entryToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listMatters } from '@/services/matterService'
import { listEventTypes } from '@/services/settingsService'
import {
  listCourtDiaryEntries,
  createCourtDiaryEntry,
  updateCourtDiaryEntry,
  deleteCourtDiaryEntry,
} from '@/services/courtDiaryService'

const STATUS_OPTIONS = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED', 'POSTPONED']
const REMINDER_MINUTE_OPTIONS = [15, 30, 60, 120, 240, 1440]

function hasCourtDiaryPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const priv = user.privileges.find((p) => p.entity === 'COURT_DIARY')
  if (!priv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return priv[map[action]] === true
}

const emptyForm = () => ({
  matterId: null,
  title: '',
  eventType: '',
  courtName: '',
  eventDate: '',
  eventTime: '',
  notes: '',
  reminder: false,
  status: 'PENDING',
  reminderMinutes: 60,
  emailNotification: true,
  smsNotification: false,
  desktopNotification: true,
  reminderSent: false,
})

export default {
  name: 'CourtDiary',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasCourtDiaryPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasCourtDiaryPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasCourtDiaryPrivilege(this.currentUser, 'delete')
    },

    statusOptions() {
      return STATUS_OPTIONS
    },

    reminderMinuteOptions() {
      return REMINDER_MINUTE_OPTIONS
    },

    eventTypeOptions() {
      return this.eventTypes.map((item) => item.typeName).filter(Boolean)
    },

    sortedEntries() {
      return [...this.entries].sort((a, b) => {
        const dateA = a.eventDate || ''
        const dateB = b.eventDate || ''
        if (dateA !== dateB) return dateA.localeCompare(dateB)
        return (a.eventTime || '').localeCompare(b.eventTime || '')
      })
    },
  },

  data() {
    return {
      entries: [],
      matters: [],
      eventTypes: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      entryToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadEntries()
    this.loadMatters()
    this.loadEventTypes()
  },

  methods: {
    async loadEntries() {
      this.loading = true
      this.error = ''

      try {
        const data = await listCourtDiaryEntries()
        this.entries = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view court diary entries.'
        } else {
          this.error = err.message || 'Failed to load court diary entries'
        }
      } finally {
        this.loading = false
      }
    },

    async loadMatters() {
      try {
        const data = await listMatters()
        this.matters = Array.isArray(data) ? data : []
      } catch {
        this.matters = []
      }
    },

    async loadEventTypes() {
      try {
        const data = await listEventTypes()
        this.eventTypes = Array.isArray(data) ? data : []
      } catch {
        this.eventTypes = []
      }
    },

    matterLabel(matterId) {
      if (!matterId) return '—'
      const matter = this.matters.find((m) => m.matterId === matterId)
      if (!matter) return `#${matterId}`
      return matter.caseTitle || matter.caseNo || `Matter #${matterId}`
    },

    statusBadgeClass(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'CONFIRMED') return 'badge-success'
      if (normalized === 'PENDING' || normalized === 'POSTPONED') return 'badge-warning'
      if (normalized === 'CANCELLED') return 'badge-danger'
      if (normalized === 'COMPLETED') return 'badge-secondary'
      return 'badge-light-primary'
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(`${value}T00:00:00`).toLocaleDateString()
    },

    formatTime(value) {
      if (!value) return '—'
      const parts = value.split(':')
      if (parts.length < 2) return value
      const hour = parseInt(parts[0], 10)
      const minute = parts[1]
      const suffix = hour >= 12 ? 'PM' : 'AM'
      const hour12 = hour % 12 || 12
      return `${hour12}:${minute} ${suffix}`
    },

    toApiTime(value) {
      if (!value) return undefined
      return value.length === 5 ? `${value}:00` : value
    },

    fromApiTime(value) {
      if (!value) return ''
      return value.slice(0, 5)
    },

    reminderLabel(minutes) {
      if (minutes === 1440) return '1 day before'
      if (minutes >= 60 && minutes % 60 === 0) {
        const hours = minutes / 60
        return `${hours} hour${hours > 1 ? 's' : ''} before`
      }
      return `${minutes} minutes before`
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    openEdit(entry) {
      this.isEditing = true
      this.editingId = entry.courtDiaryId
      this.form = {
        matterId: entry.matterId || null,
        title: entry.title || '',
        eventType: entry.eventType || '',
        courtName: entry.courtName || '',
        eventDate: entry.eventDate || '',
        eventTime: this.fromApiTime(entry.eventTime),
        notes: entry.notes || '',
        reminder: !!entry.reminder,
        status: entry.status || 'PENDING',
        reminderMinutes: entry.reminderMinutes ?? 60,
        emailNotification: entry.emailNotification !== false,
        smsNotification: !!entry.smsNotification,
        desktopNotification: entry.desktopNotification !== false,
        reminderSent: !!entry.reminderSent,
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    buildPayload() {
      const payload = {}

      const assignString = (key, value, maxLen) => {
        const trimmed = value?.trim()
        if (trimmed) payload[key] = maxLen ? trimmed.slice(0, maxLen) : trimmed
      }

      if (this.form.matterId) payload.matterId = this.form.matterId
      assignString('title', this.form.title, 255)
      assignString('eventType', this.form.eventType, 50)
      assignString('courtName', this.form.courtName, 255)
      assignString('notes', this.form.notes)

      if (this.form.eventDate) payload.eventDate = this.form.eventDate

      const eventTime = this.toApiTime(this.form.eventTime)
      if (eventTime) payload.eventTime = eventTime

      payload.reminder = !!this.form.reminder
      payload.status = this.form.status || 'PENDING'

      if (this.form.reminder) {
        payload.reminderMinutes = this.form.reminderMinutes
        payload.emailNotification = !!this.form.emailNotification
        payload.smsNotification = !!this.form.smsNotification
        payload.desktopNotification = !!this.form.desktopNotification
      }

      if (this.isEditing) {
        payload.reminderSent = !!this.form.reminderSent
      }

      return payload
    },

    async submitForm() {
      this.formError = ''
      this.saving = true

      try {
        if (this.isEditing) {
          await updateCourtDiaryEntry({
            courtDiaryId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createCourtDiaryEntry(this.buildPayload())
        }
        this.showFormModal = false
        await this.loadEntries()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(entry) {
      this.entryToDelete = entry
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteCourtDiaryEntry(this.entryToDelete.courtDiaryId)
        this.entryToDelete = null
        await this.loadEntries()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete entry'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
